/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { FileDownload } from '@/components/icons'
import { ImportPlayersModal } from '@/components/modal'
import { Search } from '@/components/search'
import { PlayerItemLoading } from '@/components/skeleton'
import { eventTracker } from '@/config/trackingFunctions'
import { CLUB_DOWNLOAD, CLUB_MODIFY, LIMIT, OFFSET } from '@/constants/Common'
import { ROUTE_CLUBS } from '@/constants/Routes'
import { TRACKING_TYPES, PAGE_NAMES } from '@/constants/TrackingConstants'
import { useDebounce } from '@/hooks/useDebounce'
import { useExportClubMembers } from '@/hooks/useExportClubMembers'
import { useAppSelector } from '@/hooks/useRedux'
import { useUserDetails } from '@/hooks/useUserDetails'
import { userSelector } from '@/pages/user/selectors'
import { useGetClubMembers } from '@/services'
import { ClubMember } from '@/types'
import { Menu, Transition } from '@headlessui/react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { clubSelector } from '../selectors'
import { ClubMemberItem } from './ClubMemberItem'

export const ClubMembers = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAppSelector(userSelector)
  const { permissions } = useAppSelector(clubSelector)

  const { clubId } = params

  const { exportMembers: handleDownloadMembers, isExporting: isDownloading, downloadRef } = useExportClubMembers(clubId)

  const userData = useUserDetails()
  const [memberSearchText, setMemberSearchText] = useState('')
  const [offset, setOffset] = useState(OFFSET)
  const [clubMembers, setClubMembers] = useState<Map<number, ClubMember>>(new Map())
  const [openImport, setOpenImport] = useState(false)
  const [lastElement, setLastElement] = useState<any>(null)
  const [currentLastElement, setCurrentLastElement] = useState<any>(null)

  const debouncedMemberSearchText = useDebounce<string>(memberSearchText || '*', 200)

  const {
    data: resClubMembers,
    isLoading,
    refetch: refetchGetClubMembers,
  } = useGetClubMembers(
    Number(clubId) || 0,
    LIMIT,
    user?.addresses?.[0]?.latitude || null,
    user?.addresses?.[0]?.longitude || null,
    offset,
    debouncedMemberSearchText || '*'
  )

  useEffect(() => {
    if (lastElement !== currentLastElement) {
      setCurrentLastElement(lastElement)
      if (resClubMembers && resClubMembers?.result?.hasMore) {
        setOffset((pre) => pre + LIMIT)
      }
    }
  }, [lastElement, resClubMembers])

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const toggleOpenImport = () => {
    setOpenImport(!openImport)
  }

  const onNavigateInvitePlayer = () => {
    navigate(`${ROUTE_CLUBS}/${clubId}/invite-player`)
  }

  const onChangeMemberSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMemberSearchText(e.target.value)
    setOffset(OFFSET)
    eventTracker(TRACKING_TYPES.SEARCH_PERFORMED, {
      'Page Name': PAGE_NAMES.CLUB_HOME,
      'Sub Page Ref': PAGE_NAMES.MEMBER_TAB,
      Keyword: e.target.value,
      ...userData,
    })
  }

  const onMemberRemoved = () => {
    setClubMembers(new Map())
    if (offset !== OFFSET) {
      setOffset(OFFSET)
    } else {
      refetchGetClubMembers()
    }
  }

  useEffect(() => {
    if (resClubMembers)
      setClubMembers((old) => {
        let temp: Map<number, ClubMember>
        if (offset === OFFSET) {
          temp = new Map()
        } else {
          temp = old
        }

        resClubMembers.result.hits.forEach((mem) => {
          temp.set(mem.id, mem)
        })

        return temp
      })
  }, [resClubMembers])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.disconnect() // Disconnect any existing observer
      currentObserver.observe(currentElement) // Observe the new last element
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement) // Clean up observer for the current element
      }
    }
  }, [lastElement, clubMembers])

  return (
    <>
      <div className="flex flex-col gap-y-8">
        <div className="flex flex-col gap-y-3">
          <h4 className="text-xl text-white font-semibold">
            {resClubMembers?.result.total}{' '}
            {resClubMembers && resClubMembers?.result.total > 1 ? t('common.members') : t('common.member')}
          </h4>

          <div className="flex items-center justify-between">
            <Search
              searchText={memberSearchText}
              onChangeInput={onChangeMemberSearch}
              className="py-2 px-3 w-56 gap-x-2"
            />
            <div className="flex items-center justify-between sm:justify-start gap-x-2">
              {has(permissions, CLUB_MODIFY) ? (
                <div className="flex gap-x-2 justify-between sm:justify-start">
                  <Menu as="div" className="relative inline-block">
                    <div>
                      <Menu.Button className="py-2 px-4 rounded-full bg-white bg-opacity-10 text-sm font-semibold text-white">
                        {t('club.invitePlayers')}
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="px-1 py-1 ">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                className={`${
                                  active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                                } group flex w-full items-center rounded-md px-2 py-2 text-sm gap-x-1`}
                                onClick={onNavigateInvitePlayer}
                              >
                                {t('club.invitePlayers')}
                              </button>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                className={`${
                                  active ? ' bg-slate-300 text-navy-900 font-semibold' : 'text-navy-900'
                                } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                onClick={toggleOpenImport}
                              >
                                {t('club.importPlayersCSV')}
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              ) : null}
              {has(permissions, CLUB_DOWNLOAD) && (
                <button
                  className="flex items-center bg-primary-500 text-sm py-2 px-4 rounded-full text-white font-semibold gap-x-2 hover:bg-opacity-70 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-opacity-100"
                  onClick={handleDownloadMembers}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FileDownload className="w-4 h-4 text-white" />
                  )}
                  {isDownloading ? 'Exporting...' : t('common.exportCSV')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-y-2">
          {Array.from(clubMembers.values())?.map((item, index: number) => {
            const isLastElement = index === clubMembers.size - 1
            return (
              <div key={item.id} ref={isLastElement ? setLastElement : null}>
                <ClubMemberItem member={item} callbackFetch={onMemberRemoved} />
              </div>
            )
          })}

          {isLoading && (
            <>
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
              <PlayerItemLoading className="!max-w-[964px]" />
            </>
          )}
          {resClubMembers && !resClubMembers?.result?.hasMore && (
            <div className="flex justify-center">
              <p className="text-slate-300 text-base font-semibold">{t('common.noMoreResults')}</p>
            </div>
          )}
        </div>
      </div>
      {clubId && <ImportPlayersModal open={openImport} onClose={toggleOpenImport} clubId={clubId} />}
      {/* Hidden anchor for in-page download (no new tab, no popup blocker) */}
      <a ref={downloadRef} className="hidden" />
    </>
  )
}
